<!-- packages/studio/src/components/army/UnitOptionsForm.vue -->
<template>
  <div class="unit-options">
    <EditableList
      :items="optionSets"
      add-label="Add option set"
      remove-label="Remove option set"
      @add="addSet"
      @remove="removeSet"
    >
      <template #row="{ index }">
        <div class="option-set">
          <StField label="Set name">
            <StTextInput
              :model-value="optionSets[index].name"
              placeholder="e.g. Wargear Options"
              @update:model-value="patchSet(index, { name: $event ?? '' })"
            />
          </StField>

          <EditableList
            :items="optionSets[index].options"
            add-label="Add option"
            remove-label="Remove option"
            @add="addOption(index)"
            @remove="removeOption(index, $event)"
          >
            <template #row="{ index: optionIndex }">
              <div class="option">
                <StField label="Option name">
                  <StTextInput
                    :model-value="optionSets[index].options[optionIndex].name"
                    placeholder="e.g. Wings"
                    @update:model-value="patchOption(index, optionIndex, { name: $event ?? '' })"
                  />
                </StField>

                <StField group label="Keywords">
                  <KeywordsInput
                    raw
                    uppercase
                    suggestion-scope="unit"
                    placeholder="e.g. PLEDGED TO KHORNE"
                    :model-value="optionSets[index].options[optionIndex].keywords"
                    @update:model-value="patchOption(index, optionIndex, { keywords: $event })"
                  />
                </StField>

                <section class="option__abilities">
                  <div class="st-eyebrow">Abilities</div>
                  <AbilityForm
                    :model-value="optionSets[index].options[optionIndex].abilities"
                    @update:model-value="patchOption(index, optionIndex, { abilities: $event })"
                  />
                </section>
              </div>
            </template>
          </EditableList>
        </div>
      </template>
    </EditableList>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { IUnit, Option as UnitOption, OptionSet as UnitOptionSet } from '@scrolldex/shared';
import { Option, OptionSet } from '@scrolldex/shared';
import AbilityForm from '../AbilityForm.vue';
import EditableList from '../EditableList.vue';
import KeywordsInput from '../KeywordsInput.vue';
import { StField, StTextInput } from '../ui/index.js';

const props = defineProps<{ modelValue: IUnit }>();
const emit = defineEmits<{ (e: 'update:modelValue', value: IUnit): void }>();

const optionSets = computed<UnitOptionSet[]>(() =>
  props.modelValue.optionSets.filter((set) => !hasWeapons(set)),
);

function hasWeapons(set: UnitOptionSet): boolean {
  return set.options.some((option) => option.weapons.length > 0);
}

function emitOptionSets(next: UnitOptionSet[]) {
  const weaponSets = props.modelValue.optionSets.filter(hasWeapons);
  emit('update:modelValue', { ...props.modelValue, optionSets: [...weaponSets, ...next] });
}

function patchSet(index: number, part: Partial<UnitOptionSet>) {
  emitOptionSets(optionSets.value.map((set, i) => (i === index ? { ...set, ...part } : set)));
}

function patchOption(setIndex: number, optionIndex: number, part: Partial<UnitOption>) {
  const sets = optionSets.value.map((set, i) => {
    if (i !== setIndex) return set;
    return {
      ...set,
      options: set.options.map((option, j) =>
        j === optionIndex ? { ...option, ...part, weapons: [] } : option,
      ),
    };
  });
  emitOptionSets(sets);
}

function addSet() {
  emitOptionSets([...optionSets.value, new OptionSet('Wargear Options', [])]);
}

function removeSet(index: number) {
  emitOptionSets(optionSets.value.filter((_, i) => i !== index));
}

function addOption(setIndex: number) {
  const sets = optionSets.value.map((set, i) => {
    if (i !== setIndex) return set;
    return { ...set, options: [...set.options, new Option('New option')] };
  });
  emitOptionSets(sets);
}

function removeOption(setIndex: number, optionIndex: number) {
  const sets = optionSets.value.map((set, i) => {
    if (i !== setIndex) return set;
    return { ...set, options: set.options.filter((_, j) => j !== optionIndex) };
  });
  emitOptionSets(sets);
}
</script>

<style scoped>
.unit-options,
.option-set,
.option {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-3);
}

.option__abilities {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-2);
}
</style>
